import { observable } from "mobx";
import { message } from "antd";
import {
  ChunkedData,
  CmrEventParams,
  IObjectMap,
} from "../../interfaces/common";
import ee from "../../services/EventEmitter";
import {
  DELETE_CMR_ENTITY,
  DOWNLOAD_CMR,
  REFRESH_CMR_TABLE,
} from "../../constants/events";
import { Cmr } from "../../models/Cmr";
import { defaultDateRange } from "../../components/declarants/dateRange";
import ApiManager from "../../mixins/ApiManager";
import downloadCmrPdf from "../../utils/downloadCmrPdf";
import { RefreshInterface } from "../interfaces/RefreshInterface";

class CmrStore<T extends Cmr>
  extends ApiManager
  implements ChunkedData, RefreshInterface {
  @observable
  data: T[] = [];

  @observable
  hasMoreItems = true;

  @observable
  isLoadingTableData = false;

  isLoadedData = false;

  @observable
  parameters: IObjectMap = {
    ...defaultDateRange,
    page: 1,
  };

  constructor() {
    super();

    ee.on(DELETE_CMR_ENTITY, (value: number) => {
      this.removeRecord(value);
    });

    ee.on(DOWNLOAD_CMR, (value: CmrEventParams) => {
      this.downloadPdf(value.id);
    });

    ee.on(REFRESH_CMR_TABLE, () => {
      this.load(
        {
          ...this.parameters,
          page: 1,
        },
        true
      );
    });
  }

  load(parameters: IObjectMap | null, needUpdateData: boolean): void {
    if (this.isLoadingTableData) {
      return;
    }

    this.isLoadingTableData = true;

    this.parameters = {
      ...this.parameters,
      ...parameters,
    };

    this.apiManager
      .getCmrs(this.parameters)
      .then(async (response) => {
        const {
          last_page: lastPage,
          current_page: currentPage,
          data,
        } = await response.json();
        this.parameters = {
          ...this.parameters,
          page: currentPage + 1,
        };

        this.data = needUpdateData ? data : this.data.concat(data);

        this.hasMoreItems = currentPage < lastPage;
      })
      .finally(() => {
        this.isLoadingTableData = false;
      });
  }

  initialLoad(): void {
    if (this.isLoadedData) {
      return;
    }

    this.isLoadedData = true;
    this.load(null, true);
  }

  removeRecord(recordId: number): void {
    this.apiManager
      .deleteCmr(recordId)
      .then(async (response) => {
        const data = await response.json();

        this.data = this.data.filter((entity) => entity.id !== recordId);

        message.success(data.message);
      })
      .catch(async (response) => {
        const data = await response.json();

        message.error(data.message);
      });
  }

  downloadPdf(recordId: number): void {
    downloadCmrPdf(this.apiManager, recordId);
  }

  refresh(): void {
    this.load(
      {
        ...this.parameters,
        page: 1,
      },
      true
    );
  }
}

export default CmrStore;
